import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-environment-connector>` says about itself.
 *
 * Production must be unmistakable. The specification says so in those words, and it is the whole
 * reason this is a component rather than two dropdowns: a tool that can point at more than one
 * environment and does not say which one is dangerous. So a production environment carries the word
 * "Production" as text on its chip, in its dropdown option, and in its accessible name — colour only
 * repeats what the text already said, because the one time colour-alone fails is the time somebody
 * runs a data load against the wrong environment.
 *
 * A chip names the environment TWICE, with part of its domain and its identifier, because every
 * sandbox of one organisation has a near-identical name and "which one is this" is exactly the
 * question that matters right before something irreversible.
 *
 * And source and target are a DIRECTION, not a pair. It is announced in that order, and the pair is
 * never reported as ready while both ends are the same environment — a comparison of something with
 * itself is not a comparison.
 */
const docs: ComponentDocs = {
  aliases: ['org picker', 'environment picker', 'org connection', 'sandbox selector', 'source and target'],

  answers: [
    'choose which two environments to compare',
    'make it obvious that one end is production',
    'swap the direction of a comparison',
    'tell two similarly named sandboxes apart',
  ],

  placements: ['appPage'],

  whenToUse:
    'Wherever an action reaches more than one environment. The production marking is the reason to ' +
    'use it rather than two dropdowns.',

  insteadUse: [
    { name: 'CompareView', when: 'the environments are chosen and the question is what differs.' },
    {
      name: 'PicklistField',
      when:
        'exactly one environment is being chosen and none of them is dangerous — this component’s ' +
        'value is the pairing and the production marking.',
    },
  ],

  inputs: [
    {
      name: 'environments',
      type: 'json',
      description:
        'What can be pointed at: `{ key, name, domain?, identifier?, production?, connected? }[]`. ' +
        '`production: true` is drawn as the WORD "Production" everywhere the environment appears.',
      default: [],
    },
    {
      name: 'source',
      attribute: 'source',
      type: 'text',
      description: 'The environment being compared FROM, by key.',
    },
    {
      name: 'target',
      attribute: 'target',
      type: 'text',
      description: 'The environment being compared TO, by key.',
    },
    {
      name: 'busy',
      attribute: 'busy',
      type: 'boolean',
      description: 'A connection is being made: the primary control reads "Connecting…" and raises nothing.',
      default: false,
    },
    {
      name: 'connectLabel',
      attribute: 'connect-label',
      type: 'text',
      description: 'What the primary control is called.',
      default: 'Compare',
    },
  ],

  events: [
    {
      name: 'caos-environment-change',
      when: 'Either end changes, including through Swap.',
      carries: '`{ source, target, touchesProduction }` — whole environments, not just keys.',
    },
    {
      name: 'caos-environment-connect',
      when: 'The primary control is pressed with a real pair chosen.',
      carries: '`{ source, target, touchesProduction }`.',
    },
  ],

  state: [
    { name: 'source', type: 'CaosEnvironment | undefined', description: 'The chosen source.' },
    { name: 'target', type: 'CaosEnvironment | undefined', description: 'The chosen target.' },
    {
      name: 'touchesProduction',
      type: 'boolean',
      description: 'Whether either end is production. Read it to gate anything irreversible.',
    },
    {
      name: 'ready',
      type: 'boolean',
      description:
        'Both ends RESOLVED against `environments` AND different. Resolved, not merely named: the ' +
        'attributes are usually set in markup while the list arrives later, and a key nothing ' +
        'answers to is not a chosen environment. A comparison of something with itself is not a ' +
        'comparison either, and offering to run one wastes somebody’s time confidently.',
    },
  ],

  parts: [
    { part: 'connector', is: 'The outer column.' },
    { part: 'end', is: 'One end — its label, its dropdown and its chip.' },
    { part: 'chip', is: 'The chosen environment’s chip.', when: 'That end has been chosen.' },
    { part: 'chip-name', is: 'The environment’s name on the chip.' },
    { part: 'chip-id', is: 'The domain and identifier on the chip.', when: 'The environment carries either.' },
    { part: 'badge', is: 'The production marking.', when: 'The environment is production.' },
    { part: 'swap', is: 'The swap control.' },
    { part: 'connect', is: 'The primary control.' },
    { part: 'summary', is: 'The "from here to here" line, and the production warning.' },
  ],

  accessibility:
    'Production is stated in THREE places — the chip badge, the dropdown option, and the chip’s ' +
    '`aria-label` — and in none of them by colour alone. The summary is a `role="status"` live ' +
    'region announcing the direction in order ("From … to …") and, when it applies, which end is ' +
    'production, so the most consequential fact on the screen is heard as well as seen. The swap ' +
    'control carries an accessible name saying what it swaps, which a two-letter icon could not. ' +
    'Each chip’s accessible name carries the name, domain, identifier and production state in one ' +
    'reading, so two near-identical sandboxes are distinguishable without sight.',

  examples: [
    {
      key: 'production-target',
      label: 'A load pointed at production',
      summary:
        'The case the component exists for. Production says so as text on the chip, in the option, ' +
        'and in the summary — the colour only repeats it.',
      placement: {
        component: 'caos-environment-connector',
        inputs: {
          source: 'uat',
          target: 'prod',
          environments: [
            { key: 'uat', name: 'UAT', domain: 'acme--uat', identifier: '00D5g000004', connected: true },
            { key: 'dev', name: 'Dev', domain: 'acme--dev', identifier: '00D5g000009', connected: true },
            { key: 'prod', name: 'Acme', domain: 'acme.my', identifier: '00D5g000001', production: true, connected: true },
          ],
        },
      },
      stage: { needsWidth: 680 },
    },
    {
      key: 'same',
      label: 'Both ends the same',
      summary:
        'Not ready, and said so. A comparison of something with itself is not a comparison, so the ' +
        'primary control stays disabled rather than offering to run one.',
      placement: {
        component: 'caos-environment-connector',
        inputs: {
          source: 'uat',
          target: 'uat',
          environments: [
            { key: 'uat', name: 'UAT', domain: 'acme--uat', identifier: '00D5g000004' },
            { key: 'dev', name: 'Dev', domain: 'acme--dev', identifier: '00D5g000009' },
          ],
        },
      },
      stage: { needsWidth: 680 },
    },
    {
      key: 'unchosen',
      label: 'Nothing chosen yet',
      summary: 'The chips appear as each end is chosen; until then the screen says what it is waiting for.',
      placement: {
        component: 'caos-environment-connector',
        inputs: {
          environments: [
            { key: 'uat', name: 'UAT', domain: 'acme--uat', identifier: '00D5g000004' },
            { key: 'prod', name: 'Acme', domain: 'acme.my', identifier: '00D5g000001', production: true },
          ],
        },
      },
      stage: { needsWidth: 680 },
    },
  ],
};

export default docs;
